import React, { Component } from 'react';

import { connect } from 'react-redux';

import { injectStripe } from 'react-stripe-elements';

import moment from 'moment';

import * as Sentry from '@sentry/browser';

import {
  updateCheckField,
  updateField,
  updatePlaidToken,
  validateCheckField,
  createPlaidAccessToken,
} from 'redux/ducks/Payment';
import { update as updateUser } from 'redux/ducks/User';
import Go4PlaidLink from "components/Go4PlaidLink";
import AchMandateModal from 'components/V2/AchMandateModal';
import { hasAchMandateConsent } from 'helpers/payment';
import isEnabledFeature, { features } from 'helpers/feature_flags';
import {
  getPendingBankLast4,
  setPendingBankLast4,
  clearPendingBankLast4,
} from 'helpers/localStorage';

const thirteenYearsAgo =
  typeof jest === 'undefined' ? moment().subtract(13, 'years').format('MM/DD/YYYY') : '01/01/2001';

const plaid_env = process.env.REACT_APP_PLAID_ENV;

export class Checking extends Component {
  state = {
    editing: true,
    DOBValid: false,
    DOBShowMsg: false,
    DOBErrorMsg: 'Invalid date input',
    mandateModalOpen: false,
    // Which Plaid entry point asked for the mandate, so only that one reopens
    // after agreeing (the connect and verify links can be mounted together).
    pendingPlaidTarget: null,
    autoOpenTarget: null,
  };

  toggleMandateModal = () => {
    this.setState((prevState) => ({
      ...prevState,
      mandateModalOpen: !prevState.mandateModalOpen,
    }));
  };

  // Blocks every Plaid entry point until the ACH authorization is agreed to,
  // showing the mandate modal instead. Returning false keeps Plaid closed.
  // Applies to micro-deposit verification too: that is equally the moment the
  // operator authorizes debits from the account, and leaving it ungated let a
  // user verify an account they could then never save.
  guardAchMandate = (target) => () => {
    // 'connect' starts a link against a bank account we haven't authorized yet,
    // so it always needs fresh agreement — a mandate covers one specific
    // account and never carries over from another.
    //
    // 'verify' finishes the account already linked in this attempt, so consent
    // recorded for it (this session's agreement, or one already on file) counts
    // and there's nothing to re-ask.
    const authorized =
      !isEnabledFeature(features.ACH_MANDATE_TC_UPDATE, this.props.user) ||
      (target === 'verify'
        ? hasAchMandateConsent({ user: this.props.user, stripeTerms: this.props.stripeTerms })
        : !!this.props.stripeTerms);

    if (authorized) return true;

    this.setState((prevState) => ({
      ...prevState,
      mandateModalOpen: true,
      pendingPlaidTarget: target,
    }));
    return false;
  };

  // Persist the agreement immediately rather than waiting for the payment method
  // to be saved: an operator who agrees and then abandons the flow (or waits days
  // on micro-deposits) would otherwise have their consent silently discarded.
  // The backend ignores this for operators it can't yet attach a mandate to
  // (brand-new ACH, no Stripe bank source and no Plaid link yet); those are
  // captured by acceptAchMandateEpic once the source is attached at save.
  //
  // Then continue straight into Plaid, so agreeing doesn't dead-end on having to
  // press the button a second time.
  onMandateAccepted = () => {
    this.props.onTermsChange(true);
    this.props.recordAchMandate(this.props.user.id);
    this.setState((prevState) => ({
      ...prevState,
      autoOpenTarget: prevState.pendingPlaidTarget,
    }));
  };

  onPlaidAutoOpened = () => {
    this.setState((prevState) => ({ ...prevState, autoOpenTarget: null }));
  };

  componentWillMount() {
    if (this.props.loadedSource && !this.props.loadingSource) {
      if (this.hasSource() && !this.props.publicToken) {
        this.setState({ ...this.state, editing: false });
      } else {
        this.setState({ ...this.state, editing: true });
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.loadedSource &&
      !this.props.loadingSource &&
      prevProps.publicToken !== this.props.publicToken
    )
      this.setState({ ...this.state, editing: !!this.props.publicToken });
  }

  hasSource = () => {
    return this.props.source.id && this.props.source.object === 'bank_account';
  };

  // While a micro-deposit verification is pending (publicToken set) there is no
  // Stripe source yet, so fall back to the mask captured when the account was connected.
  displaySource = () => {
    if (this.hasSource()) return this.props.source;
    const last4 = this.props.publicToken && getPendingBankLast4(this.props.user.id);
    return last4 ? { last4 } : null;
  };

  onFieldBlur = (field) => {
    if (field === 'dateOfBirth') {
      if (this.state.DOBValid) {
        this.setState({ ...this.state, DOBShowMsg: false });
      } else {
        this.setState({ ...this.state, DOBShowMsg: true });
      }
    }

    this.props.validate(field);
  };

  handleOnSuccess = (public_token, metadata) => {
    if (metadata.account.verification_status === 'pending_manual_verification') {
      setPendingBankLast4(this.props.user.id, metadata.account.mask);
      this.props.createPlaidAccessToken(public_token, metadata.account_id, this.props.user.id);
      window.scroll(0, 0);
    } else {
      clearPendingBankLast4();
      this.props.updatePlaidToken(public_token, metadata.account_id);
      this.props.validate('token');
    }
  };

  handleOnExit = (err, metadata) => {
    // The user exited the Link flow.
    Sentry.withScope((scope) => {
      scope.setUser({
        id: this.props.user.id,
        email: this.props.user.email,
        first_name: this.props.user.firstName,
        last_name: this.props.user.lastName,
      });
      scope.setExtra('err', err);
      scope.setExtra('metadata', metadata);
      Sentry.captureMessage(
        `Plaid onExit() called with non-empty err for user_id=${this.props.user.id}`
      );
    });
    if (err != null) {
      // The user encountered a Plaid API error prior to exiting.
      Sentry.withScope((scope) => {
        scope.setUser({
          id: this.props.user.id,
          email: this.props.user.email,
          first_name: this.props.user.firstName,
          last_name: this.props.user.lastName,
        });
        scope.setExtra('err', err);
        scope.setExtra('metadata', metadata);
        Sentry.captureMessage(
          `Plaid onExit() called with non-empty err for user_id=${this.props.user.id}`
        );
      });
    }
  };

  onStripeFieldBlur = (field) => {
    this.props.validate(field);
    this.updateStripeToken();
  };

  updateValidation = (fieldName, condition) => {
    let newval = false;
    if (condition) {
      newval = true;
    }

    this.setState({ ...this.state, [fieldName]: newval });
  };

  onFieldChange = (field, event) => {
    const value = event.currentTarget.value;
    if (field === 'dateOfBirth') {
      const dateInput = moment(value, 'MM/DD/YYYY');
      this.updateValidation('DOBValid', dateInput._isValid && !dateInput.isAfter(thirteenYearsAgo));
    }

    this.props.updateField(field, event.currentTarget.value);
  };

  toggleEditing = () => {
    this.setState({ ...this.state, editing: !this.state.editing });
  };

  updateStripeToken = () => {
    if (
      this.props.check.accountType &&
      this.props.check.accountNumber &&
      this.props.check.routingNumber
    ) {
      this.props.stripe
        .createToken('bank_account', {
          account_holder_name: `${this.props.user.firstName} ${this.props.user.lastName}`,
          account_holder_type: this.props.check.accountType,
          account_number: this.props.check.accountNumber,
          country: 'US',
          currency: 'usd',
          routing_number: this.props.check.routingNumber,
        })
        .then((result) => this.props.updateToken(result.token));
    }
  };

  plaidLinkStyles = (variant = 'default') => {
    const base = {
      'padding': '10px 45px',
      'outline': 'none',
      'border-radius': '5px',
      'width': 'auto',
      'font-size': '15px',
    };
    if (variant === 'white') {
      return {
        ...base,
        'background': 'white',
        'color': '#55556d',
        'border': '2px solid #55556d',
      };
    }
    return {
      ...base,
      'background': '#55556d',
      'color': '#fff',
      'border': 'none',
    };
  };

  // Always mounted so the Plaid link token is fetched up front — that way, once
  // the mandate is agreed to, Plaid can open immediately instead of the operator
  // waiting on a load. Opening is gated behind the ACH authorization.
  renderPlaidConnect = ({ target, label, style, className, token }) => (
    <Go4PlaidLink
      clientName="Go4Ellis"
      env={plaid_env}
      product={['auth']}
      selectAccount={true}
      user={{
        legalName: this.props.user.firstName + ' ' + this.props.user.lastName,
        emailAddress: this.props.user.email,
      }}
      token={token}
      onExit={this.handleOnExit}
      onSuccess={this.handleOnSuccess}
      onBeforeOpen={this.guardAchMandate(target)}
      autoOpen={this.state.autoOpenTarget === target}
      onAutoOpened={this.onPlaidAutoOpened}
      style={style}
      className={className}
    >
      {label}
    </Go4PlaidLink>
  );

  render() {
    const displaySource = this.displaySource();
    const sectionClass =
      displaySource || (this.props.source.id && this.props.source.object === 'card')
        ? 'section no-dashed-line'
        : 'section';

    return (
      <div className="checking">
        <div className={sectionClass}>
          <h3>payment information</h3>
          <p>
            All financial transactions are processed securely through{' '}
            <a href="https://stripe.com/" target="_blank" rel="noopener noreferrer">
              Stripe
            </a>
            {'.'}
          </p>
          <p>
            Bank account verification is processed securely through{' '}
            <a href="https://plaid.com/" target="_blank" rel="noopener noreferrer">
              Plaid
            </a>
            {'.'}
          </p>

          {displaySource && (
            <div className="source-info">
              <p>Account Information on File:</p>
              <div className="row">
                <div className="col-md-5">
                  <i>Current Account Number:</i>
                  <span>xxxx xxxx xxxx {displaySource.last4}</span>
                </div>
                {displaySource.routingNumber && (
                  <div className="col-md-4">
                    <i>Routing Number:</i>
                    <span>{displaySource.routingNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {(!this.props.user.lockPaymentMethod && this.state.editing) ? (
            <div className="row">
              {this.props.check.plaid.token && (
                <div className="col-md-7 d-flex align-items-center">
                  <span className="responsive-text">
                    Bank account connected! Please click the <strong>'UPDATE PAYMENT'</strong>{' '}
                    button to save
                  </span>
                </div>
              )}
              {!this.props.check.plaid.token && this.props.publicToken && (
                <div className="btn-wrapper col-md-6">
                  {this.renderPlaidConnect({
                    target: 'verify',
                    label: 'VERIFY YOUR MICRO DEPOSITS',
                    style: this.plaidLinkStyles(),
                    className: 'btn btn-micro gotham-bold-font',
                    token: this.props.publicToken,
                  })}
                </div>
              )}
              {!this.props.check.plaid.token && (
                <div className="col-md-6">
                  {this.renderPlaidConnect({
                    target: 'connect',
                    label: this.props.publicToken
                      ? 'CONNECT A DIFFERENT ACCOUNT'
                      : 'CONNECT A BANK ACCOUNT',
                    style: this.plaidLinkStyles(this.props.publicToken ? 'white' : 'default'),
                    className: this.props.publicToken ? 'toggle-button-white' : 'toggle-button',
                  })}
                </div>
              )}
            </div>
          ) : (
            !this.props.user.lockPaymentMethod && (
              <div className="col-md-6 px-0">
                {this.renderPlaidConnect({
                  target: 'connect',
                  label: 'CONNECT A BANK ACCOUNT',
                  style: this.plaidLinkStyles(),
                  className: 'toggle-button',
                })}
              </div>
            )
          )}
        </div>

        <AchMandateModal
          isOpen={this.state.mandateModalOpen}
          toggle={this.toggleMandateModal}
          onAccept={this.onMandateAccepted}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: state.session.currentUser,
    check: state.payment.check,
    loadedSource: state.payment.loadedSource,
    loadingSource: state.payment.loadingSource,
    source: state.payment.source,
    states: state.enums.usStates,
    validation: state.payment.validation.check,
    publicToken: state.payment.publicToken,
    object: state.payment.object,
    stripeTerms: state.payment.stripeTerms,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updateField: (field, value) => dispatch(updateCheckField(field, value)),
    updatePlaidToken: (token, account_id) => dispatch(updatePlaidToken(token, account_id)),
    createPlaidAccessToken: (token, account_id, id) =>
      dispatch(createPlaidAccessToken(token, account_id, id)),
    validate: (field) => dispatch(validateCheckField(field)),
    onTermsChange: (value) => dispatch(updateField('stripeTerms', value)),
    // id + flag only — serializing the whole user PATCHes the entire profile and
    // phone_no is not round-trip safe.
    recordAchMandate: (userId) => dispatch(updateUser({ id: userId, acceptAchMandate: true })),
  };
}

export default injectStripe(connect(mapStateToProps, mapDispatchToProps)(Checking));
